import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ExistingProvider,
    forwardRef,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    SimpleChange,
    SimpleChanges
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as _ from 'lodash-es';
import {BehaviorSubject, fromEvent, NEVER} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../destroyable';

export interface SwitchState<T = any> {
    label: string;
    value: T;
    classes?: string;
    activeClasses?: string;
}

export interface SwitchInputChanges extends SimpleChanges {
    config: SimpleChange;
}

const SWITCH_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SwitchComponent),
    multi: true
};

const ACTIVE_CLASS = '-active';
const BEFORE_ACTIVE_CLASS = '-before-active';
const AFTER_ACTIVE_CLASS = '-after-active';

@Component({
    selector: 'switch',
    templateUrl: './switch.component.html',
    styleUrls: ['./switch.component.scss'],
    providers: [SWITCH_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SwitchComponent implements ControlValueAccessor, OnInit, OnChanges {

    @Input()
    public config: SwitchState[];

    @Input()
    public set labelElement(labelElement: HTMLElement) {
        this.labelElement$.next(labelElement);
    }

    public readonly destroy$ = destroy$();

    public stateClasses: string[] = [];

    @HostBinding('class.-disabled')
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    @HostBinding('attr.role')
    public hostRole: string = 'radiogroup';

    public activeState: SwitchState;

    private innerValue: any = '';

    private readonly labelElement$ = new BehaviorSubject<HTMLElement>(null);

    constructor(
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        this.listenToLabelEvents();
    }

    public ngOnChanges(changes: SwitchInputChanges): void {
        if (changes.config) {
            this.setActiveStateByValue(this.innerValue);
        }
    }

    public writeValue(value: any): void {
        this.innerValue = value;
        this.setActiveStateByValue(value);
    }

    public propagateChange(value: any): void {
        // To be overridden by parent via registerOnChange.
    }

    public registerOnChange(handler: (value: any) => void): void {
        this.propagateChange = handler;
    }

    public registerOnTouched(handler: () => void): void {
        // Not implemented.
    }

    public onStateClick(state: SwitchState): void {
        if (!this.isDisabled && state !== this.activeState) {
            this.activeState = state;
            this.writeValue(state.value);
            this.propagateChange(state.value);
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled || null; // Null is required in order to remove "disabled" attribute.
    }

    protected setActiveState(state: SwitchState): void {
        this.activeState = state;

        const activeStateValue = state && state.value;

        this.stateClasses = this.config.reduce((statesClasses, currentState, stateIndex) => {
            statesClasses[stateIndex] = statesClasses[stateIndex] || [];
            statesClasses[stateIndex].push(this.config[stateIndex].classes || '');

            if (currentState.value === activeStateValue) {
                statesClasses[stateIndex].push(ACTIVE_CLASS, this.activeState.activeClasses || '');

                if (stateIndex) {
                    statesClasses[stateIndex - 1].push(BEFORE_ACTIVE_CLASS);
                }

                if (stateIndex < this.config.length - 1) {
                    statesClasses[stateIndex + 1] = [AFTER_ACTIVE_CLASS];
                }
            }

            return statesClasses;
        }, new Array(this.config.length));

        this.changeDetectorRef.markForCheck();
    }

    protected setActiveStateByValue(value: any): void {
        const isActualState = this.activeState && this.activeState.value === value;

        const activeState = isActualState
            ? this.activeState
            : _.find(this.config, (state: SwitchState) => state.value === value);

        this.setActiveState(activeState);
    }

    private listenToLabelEvents(): void {
        this.labelElement$.pipe(
            switchMap(element => element
                ? fromEvent(element, 'click')
                : NEVER
            ),
            takeUntil(this.destroy$),
            tap(() => this.nextState())
        ).subscribe();
    }

    private nextState(): void {
        const stateCount = this.config && this.config.length || 0;

        if (stateCount) {
            const activeStateIndex = _.findIndex(this.config, this.activeState);
            const nextStateIndex = (activeStateIndex + 1) % stateCount;

            this.onStateClick(this.config[nextStateIndex]);
        }
    }
}
